/**
 *
 * AssignmentLandingPage
 *
 */
import React from 'react';
import { PageBase } from 'app/components/Common/PageBase';
import {
  Stack,
  Typography,
  Card,
  Button,
} from '@mui/joy';
import { useDispatch } from 'react-redux';
import { synchronizeFetchExams } from 'store/ExamStore/slice';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import { PageBodyContainer } from 'app/components/Common/PageBodyContainer';
import useExam from './utils/useExam';
import { headExam } from './utils/headExam';
import HeaderTitle from 'app/components/HeaderTitle/HeaderTitle';
import { TypeExamProps } from './utils/types';
import { ExamCard } from 'app/components/ExamCard/ExamCard';
import { useUserCount } from 'app/hooks/useUserCount';
import LoadingCircular from 'app/components/LoadingCircular/LoadingCircular';
import { useSavedScroll } from './utils/useSavedScroll';
import { ExamHeader } from './components/ExamHeader/ExamHeader';

export function ExamPage({
  contentOnly,
  sx,
  filterCountdown,
  upcomingExam,
  getLength,
  slQuota,
  hlQuota,
}: TypeExamProps) {

  const dispatch = useDispatch();

  const {
    subject,
    paper,
    level,
    searchQuery,
    sortBy,
    isExpanded,
    statusFilter,
    dataExam,
    setSubject,
    setPaper,
    setLevel,
    setSearchQuery,
    setSortBy,
    setIsExpanded,
    setStatusFilter,
    isLoading,
    hasError,
  } = useExam({ filterCountdown, getLength });

  const { userCount, showUserCount } = useUserCount();

  const isDataLoaded = !isLoading && dataExam !== null;
  useSavedScroll(isDataLoaded);

  const ComponentMain = (
    <Stack sx={sx}>
      <ExamHeader
        upcomingExam={upcomingExam}
        subject={subject}
        level={level}
        searchQuery={searchQuery}
        sortBy={sortBy}
        isExpanded={isExpanded}
        statusFilter={statusFilter}
        setSubject={setSubject}
        setPaper={setPaper}
        setLevel={setLevel}
        setSearchQuery={setSearchQuery}
        setSortBy={setSortBy}
        setIsExpanded={setIsExpanded}
        setStatusFilter={setStatusFilter}
        userCount={userCount}
        showUserCount={showUserCount}
      />

      {
        upcomingExam &&
        <>
          <HeaderTitle
            subtitle={headExam.upcomingExam.subtitle}
            title={headExam.upcomingExam.title}
          />
          <Card
            variant="soft"
            color="primary"
            sx={{
              mt: 2,
              mb: 2,
              p: 2,
              borderRadius: '12px'
            }}
          >
            <Typography level="body-md">
              ⏰ The exam waiting room will open 30 minutes prior to the scheduled time.
            </Typography>
          </Card>
        </>
      }

      <Stack spacing={2}>
        <Stack sx={{ mt: 1, gap: 2 }}>
          {isLoading && (!dataExam || dataExam.length === 0) ? (
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              py={4}
            >
              <LoadingCircular />
              <Typography level="body-md" color="neutral" sx={{ mt: 1 }}>
                Loading exam data...
              </Typography>
            </Stack>
          ) : dataExam?.length > 0 ? (
            dataExam.map((exam, index) => (
              <ExamCard
                key={index}
                exam={exam}
                isLoading={isLoading} // Pass loading state to ExamCard
              />
            ))
          ) : hasError ? (
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              py={4}
            >
              <Typography level="body-md" color="danger" sx={{ mt: 1 }}>
                Error loading exam data. Please try again.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => dispatch(synchronizeFetchExams())}
              >
                Retry
              </Button>
            </Stack>
          ) : (
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              py={4}
              sx={upcomingExam ? { backgroundColor: '#F4F7FF', borderRadius: '1.5rem', px: 1, } : {}}
            >
              <ArticleRoundedIcon
                sx={{ fontSize: 48, color: 'grey.400' }}
              />
              {upcomingExam ? (
                <Stack justifyItems={'center'} alignItems={'center'}>
                  <Typography level="body-lg" color="neutral">
                    No upcoming exam at the moment...
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Typography level="body-lg" color="neutral">
                    No exam found
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    {searchQuery || subject || paper
                      ? 'Try adjusting your filters'
                      : 'Add your first exam using the buy exam button above'}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <>
      {
        contentOnly ?
          ComponentMain
          :
          <PageBase
            breadcrumbs={[
              {
                text: 'My Exam',
              },
            ]}
          >
            <PageBodyContainer sx={{ maxWidth: 900, p: 2, }}>
              {ComponentMain}
            </PageBodyContainer>
          </PageBase>

      }
    </>
  );
}
